import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, UrlSegment } from '@angular/router';
import { loadClassroomParticipants } from '@app/classrooms/_store/participants';
import { ClassroomTabsEnum, IClassroomTabOption } from '@app/classrooms/classroom-page/models';
import { WebsocketService } from '@app/core/websocket';
import { setCurrentItem } from '@app/store/actions/content.actions';
import {
  AuthService,
  ClassroomsService,
  ContentService,
  ContextService,
  FeaturesService,
  FilesService,
  MetaService,
  PlatformService,
  UserServiceContractsService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { getMyPins } from '@store/reducers/profile.reducer';
import {
  AnyType,
  Classroom,
  ClassroomParticipant,
  FeatureEnum,
  IContentFilterBlock,
  IEntityUpdate,
  ImageSizeEnum,
  SignupUserServiceContractRequest,
  User,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonState } from 'onclass-ui';
import { catchError, delay, filter, mergeAll, Observable, of, takeUntil, tap } from 'rxjs';
import { clearClassroomMeetings, loadClassroomMeetings } from '../_store/meetings';
import { classroomDetails, tabs } from './constants';
import { ChatService } from '@app/core/services/chat.service';

@Component({
  selector: 'app-classroom-page',
  templateUrl: './classroom-page.component.html',
  styleUrls: ['./classroom-page.component.scss'],
})
export class ClassroomPageComponent extends ChangableComponent implements OnInit, OnDestroy {
  public loading = true;
  public pins$ = this.store.select(getMyPins);
  public classroom: Classroom;
  public inviteModal = false;
  public meetingModal = false;
  public isTeacher = false;
  public classroomRoute = ClassroomsService.classroomRoute;
  public joinState: OnUiButtonState = 'default';
  public me: User;

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private classroomsService: ClassroomsService,
    private meta: MetaService,
    private contextService: ContextService,
    private chatService: ChatService,
    private contractsService: UserServiceContractsService,
    private features: FeaturesService,
    private authService: AuthService,
    private store: Store,
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    protected readonly websocketService: WebsocketService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit(): void {
    of(...[this.watchNavigation$, this.watchRouteClassroom$, this.watchClassroomChanged$, this.watchMe$])
      .pipe(
        mergeAll(),
        tap(() => this.detectChanges()),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public ngOnDestroy() {
    this.meta.reset();
    this.stopWatching();
    super.ngOnDestroy();
  }

  public get tabs(): IClassroomTabOption[] {
    return tabs.filter((tab) => {
      switch (tab.code) {
        case ClassroomTabsEnum.content:
          // TODO ?? (this.classroom?.visit_info?.is_participant && this.hasContent) || ...
          return (
            (this.classroom?.visit_info?.is_participant || this.classroom?.can?.manage_content) &&
            this.isAvailablePublications
          );

        case ClassroomTabsEnum.participants:
          return this.classroom?.visit_info?.is_participant; // TODO classroom?.can?.manage_participants

        case ClassroomTabsEnum.meetings:
          return this.classroom?.visit_info?.is_participant || this.classroom?.can?.schedule_meeting;

        default:
          return false;
      }
    });
  }

  public openInviteModal() {
    this.inviteModal = true;
    this.detectChanges();
  }

  public closeInviteModal() {
    this.inviteModal = false;
    this.detectChanges();
  }

  public onParticipantsInvited(participants: ClassroomParticipant[]) {
    if (this.router.url.includes('/participants')) {
      this.store.dispatch(loadClassroomParticipants());
    }
  }

  public openMeetingModal() {
    this.meetingModal = true;
    this.detectChanges();
  }

  public closeMeetingModal() {
    this.meetingModal = false;
    this.detectChanges();
  }

  public onMeetingSaved(event: boolean): void {
    this.meetingModal = false;
    this.onChanged();
  }

  public onClassroomJoin() {
    if (this.isGuest) {
      this.authService.showAuthModal(() => {
        this.onChanged();
        this.processJoin$$('classroom_join', this.classroomsService.acceptInvitation(this.classroom.id));
      });
    } else {
      this.processJoin$$('classroom_join', this.classroomsService.acceptInvitation(this.classroom.id));
    }
  }

  public get isGuest() {
    return !this.authService.isAuthenticated;
  }

  public onChanged() {
    this.store.dispatch(clearClassroomMeetings());
    this.store.dispatch(loadClassroomMeetings());
    this.detectChanges();
  }

  public onContractSignup() {
    const request = new SignupUserServiceContractRequest({
      user_service_contract_id: this.classroom.user_service_contract_id,
      currency_id: this.me.currency_id,
    });
    this.processJoin$$('contract_signup', this.contractsService.signup(request));
  }

  protected processJoin$$(action: string, request$: Observable<AnyType>) {
    this.joinState = 'progress';
    this.detectChanges();

    return request$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (res) => {
        this.store.dispatch(loadClassroomParticipants());
        this.joinState = 'done';
        this.setTimeout(() => {
          this.joinState = 'default';
          this.detectChanges();
        }, 3000);
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.joinState = 'fail';
        this.detectChanges();
      },
    });
  }

  public openChat() {
    const thread_id = this.classroom.id; // TODO this.classroom.chat_thread_id
    this.chatService.openTreadChat(thread_id);
  }

  public link(item: IContentFilterBlock): UrlSegment {
    return new UrlSegment(item.code, {});
  }

  private setupMeta() {
    const title = this.classroomsService.classroomFullTitle(this.classroom);
    this.meta.setTitle([title, 'classrooms.meta-title']);
    if (this.classroom.description) {
      this.meta.setDescription(this.classroom.description);
    }
    if (this.classroom.cover_id) {
      this.meta.setImage(this.classroom.cover_id);
    }
  }

  private startWatching() {
    if (this.isBrowser) {
      this.websocketService.startWatching('classroom', this.classroom.id);
    }
  }

  private stopWatching() {
    this.websocketService.stopWatching('classroom', this.classroom.id);
  }

  private reloadClassroom() {
    this.classroomsService
      .getClassroomById(this.classroom.id, classroomDetails)
      .pipe(
        tap((classroom) => this.updateClassroom(classroom)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private updateClassroom(classroom: Classroom) {
    this.loading = false;
    this.classroom = classroom;
    this.store.dispatch(setCurrentItem({ item: this.classroom }));
    if (!this.classroom.id) {
      return;
    }
    this.setupMeta();
    this.checkHasContent();
    this.detectChanges();
  }

  private checkHasContent() {}

  private get watchNavigation$() {
    return this.router.events.pipe(filter((event) => event instanceof NavigationEnd));
  }

  private get watchRouteClassroom$() {
    return this.route.paramMap.pipe(
      tap(() => {
        this.updateClassroom(this.route.snapshot.data.classroom);
        this.startWatching();
      }),
    );
  }

  private get watchClassroomChanged$() {
    if (this.isServer) {
      return of(false);
    }
    return this.websocketService.on<IEntityUpdate>('update').pipe(
      filter((update) => update.entity === 'classroom' && update.id === this.classroom.id),
      tap(() => this.reloadClassroom()),
    );
  }

  protected get watchMe$() {
    return this.contextService.me$.pipe(tap((me) => (this.me = me)));
  }

  public get isAvailableChat(): boolean {
    return this.features.isAvailable(FeatureEnum.chat);
  }

  public get isAvailablePublications(): boolean {
    return this.features.isAvailable(FeatureEnum.profile_publications);
  }

  public trackByFn(index) {
    return index;
  }
}
